(() => {
  'use strict';
  if (window.torqueCartDrawer || !window.fetch || !window.HTMLDialogElement) return;
  let busy = false;
  let opener = null;
  let closeTimer;
  let previousOverflow = '';
  let refreshVersion = 0;
  let changed = false;
  let confettiFrame;
  let confettiTimer;
  let confetti;
  let latestAddedItem = null;
  let comboOrder = null;
  let fallbackQueue = Promise.resolve();
  let fallbackPending = 0;
  let pendingFocus = null;
  let currentCodes = null;
  let refreshQueued = false;
  let refreshRequested = false;
  let needsRecovery = false;
  const pairCode = '2NDPAIR';
  const pairCodeKey = 'torque:2ndpair-auto';
  let pairCodePending = false;
  const session = (action, value) => { try { return action === 'get' ? sessionStorage.getItem(pairCodeKey) : action === 'set' ? sessionStorage.setItem(pairCodeKey, value) : sessionStorage.removeItem(pairCodeKey); } catch { return null; } };
  async function applyPairCode() {
    const items = drawer()?.querySelector('[data-drawer-pairs]');
    if (!items || busy || needsRecovery || pairCodePending) return;
    if ((Number(items.dataset.drawerPairs) || 0) < 2) { session('remove'); return; }
    const codes = (items.dataset.drawerCodes || '').split(',').map(code => code.trim()).filter(Boolean);
    // Once per session, so a shopper who removes the code is not overridden.
    if (session('get') || codes.some(code => code.toUpperCase() === pairCode)) return;
    session('set', '1');
    pairCodePending = true;
    try { await mutate('cart/update.js', () => ({ discount: [...(currentCodes || codes), pairCode].filter((code, index, all) => all.indexOf(code) === index).join(',') }), 'discount'); }
    finally { pairCodePending = false; }
  }
  function shuffleCombos(root) {
    const grid = root?.querySelector('[data-drawer-rec-grid]');
    if (!grid) return;
    const tiles = [...grid.querySelectorAll(':scope > [data-drawer-combo]')];
    if (!comboOrder) {
      comboOrder = tiles.map(tile => tile.dataset.drawerCombo);
      for (let index = comboOrder.length - 1; index > 0; index--) {
        const swap = Math.floor(Math.random() * (index + 1));
        [comboOrder[index], comboOrder[swap]] = [comboOrder[swap], comboOrder[index]];
      }
    }
    const rank = new Map(comboOrder.map((id, index) => [id, index]));
    const position = tile => rank.get(tile.dataset.drawerCombo) ?? comboOrder.length;
    grid.prepend(...tiles.sort((a, b) => position(a) - position(b)));
  }
  function clearConfetti() {
    cancelAnimationFrame(confettiFrame);
    clearTimeout(confettiTimer);
    confetti?.remove();
    confetti = null;
  }
  function celebrateAdd(item) {
    clearConfetti();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    confettiTimer = setTimeout(() => {
      confettiFrame = requestAnimationFrame(() => {
        const lines = [...drawer().querySelectorAll('[data-drawer-key]')];
        const key = item?.key;
        const variantId = item?.variantId ?? item?.variant_id ?? item?.id;
        const line = lines.find(node => key && node.dataset.drawerKey === String(key))
          || lines.find(node => variantId != null && node.dataset.drawerVariantId === String(variantId));
        const media = line?.querySelector('.tq-drawer__media');
        if (!media) return;
        confetti = document.createElement('div');
        confetti.className = 'tq-cart-confetti';
        confetti.setAttribute('aria-hidden', 'true');
        const colors = ['#0076BD', '#FFBE00', '#FF691F', '#55BBE5'];
        for (let index = 0; index < 24; index++) {
          const piece = document.createElement('i');
          const spread = (index / 23 - 0.5) * 150;
          piece.style.setProperty('--x', `${spread}px`);
          piece.style.setProperty('--peak', `${-28 - Math.random() * 38}px`);
          piece.style.setProperty('--fall', `${35 + Math.random() * 45}px`);
          piece.style.setProperty('--turn', `${(index % 2 ? 1 : -1) * (180 + Math.random() * 360)}deg`);
          piece.style.background = colors[index % colors.length];
          confetti.append(piece);
        }
        media.append(confetti);
        confettiTimer = setTimeout(clearConfetti, 1500);
      });
    }, 1000);
  }
  const drawer = () => document.getElementById('TorqueCartDrawer');
  const themeMode = () => drawer()?.dataset.cartMode !== 'external';
  const publish = (type, detail) => window.TorqueIntegrations?.publish(type, detail);
  function presentExternal(source) {
    const cartUrl = drawer()?.dataset.cartUrl || '/cart';
    const event = new CustomEvent('torque:cart:open-request', { cancelable: true, detail: { source, cartUrl } });
    if (document.dispatchEvent(event)) window.location.assign(cartUrl);
  }
  const labels = () => JSON.parse(drawer()?.querySelector('[data-drawer-labels]')?.textContent || '{}');
  const endpoint = path => `${(drawer()?.dataset.rootUrl || '/').replace(/\/?$/, '/')}${path}`;
  const status = (message, error = false) => {
    const node = drawer()?.querySelector('[data-drawer-status]');
    if (!node) return;
    node.textContent = message;
    node.classList.toggle('is-error', error || needsRecovery);
    if (needsRecovery) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.dataset.drawerRetry = '';
      retry.className = 'tq-drawer__retry';
      retry.textContent = labels().retry || 'Retry cart update';
      retry.disabled = busy || cartPending() > 0;
      node.append(' ', retry);
    }
  };
  const cartPending = () => Number(window.TorqueIntegrations?.cart?.pending) || fallbackPending;
  const locked = () => busy || cartPending() > 0 || needsRecovery;
  function rememberFocus(target = document.activeElement) {
    const node = drawer();
    if (!node?.contains(target)) return null;
    const line = target.closest('[data-drawer-key]');
    const combo = target.closest('[data-drawer-combo]');
    const merch = target.closest('[data-merch-product]');
    return {
      key: line?.dataset.drawerKey,
      group: line?.dataset.mobileCartGroup,
      control: line ? [...line.querySelectorAll('[data-drawer-quantity]')].indexOf(target) : -1,
      combo: combo?.dataset.drawerCombo,
      merch: merch?.dataset.merchProduct,
      select: target.matches('select'),
    };
  }
  function restoreFocus(memo) {
    const node = drawer();
    const activeModal = document.activeElement?.closest('dialog[open], [role="dialog"][aria-modal="true"]');
    if (!memo || !node?.open || (activeModal && activeModal !== node)) return true;
    const find = (selector, field, value) => [...node.querySelectorAll(selector)].find(item => item.dataset[field] === value);
    const line = memo.key && [...node.querySelectorAll('[data-drawer-key]')].find(item => !item.hidden && ((memo.group && item.dataset.mobileCartGroup === memo.group) || item.dataset.drawerKey === memo.key));
    const card = memo.combo && find('[data-drawer-combo]', 'drawerCombo', memo.combo)
      || memo.merch && find('[data-merch-product]', 'merchProduct', memo.merch);
    let target = line?.querySelectorAll('[data-drawer-quantity]')[memo.control]
      || card?.querySelector(memo.select ? 'select[name="id"]' : 'button[type="submit"]')
      || node.querySelector('[data-drawer-close]');
    if (target?.disabled) {
      if (locked() && target.matches('[data-drawer-quantity], [name="checkout"]')) return false;
      target = node.querySelector('[data-drawer-close]');
    }
    target?.focus({ preventScroll: true });
    return true;
  }
  function updateBusyControls() {
    const value = locked();
    drawer()?.setAttribute('aria-busy', String(busy || cartPending() > 0));
    drawer()?.querySelector('a.tq-drawer__checkout')?.setAttribute('aria-disabled', String(value));
    drawer()?.querySelectorAll('[data-drawer-quantity], [name="checkout"]').forEach(button => {
      if (value) {
        if (!('wasDisabled' in button.dataset)) button.dataset.wasDisabled = String(button.disabled);
        button.disabled = true;
      } else if ('wasDisabled' in button.dataset) {
        button.disabled = button.dataset.wasDisabled === 'true';
        delete button.dataset.wasDisabled;
      }
    });
    const retry = drawer()?.querySelector('[data-drawer-retry]');
    if (retry) retry.disabled = busy || cartPending() > 0;
    if (!value && pendingFocus && restoreFocus(pendingFocus)) pendingFocus = null;
  }
  const setBusy = value => {
    busy = value;
    updateBusyControls();
  };
  function runCartTask(task) {
    if (window.TorqueIntegrations?.cart?.run) return window.TorqueIntegrations.cart.run(task);
    fallbackPending++;
    updateBusyControls();
    const result = fallbackQueue.catch(() => {}).then(task);
    fallbackQueue = result.catch(() => {});
    return result.finally(() => { fallbackPending--; updateBusyControls(); });
  }
  function scheduleRefresh() {
    refreshRequested = true;
    if (refreshQueued) return;
    refreshQueued = true;
    void runCartTask(async () => {
      refreshRequested = false;
      try { await refresh(); if (!needsRecovery) status(''); } catch { status(labels().error, true); }
    }).finally(() => {
      refreshQueued = false;
      if (refreshRequested) scheduleRefresh();
    });
  }
  function open() {
    const node = drawer();
    if (!node) return;
    clearTimeout(closeTimer);
    if (!node.open) {
      opener = document.activeElement;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      node.showModal();
      node.querySelector('[data-drawer-close]')?.focus();
    }
    requestAnimationFrame(() => node.classList.add('is-visible'));
  }
  function close() {
    const node = drawer();
    if (!node?.open) return;
    clearConfetti();
    node.classList.remove('is-visible');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      node.close();
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      if (changed) { document.dispatchEvent(new CustomEvent('torque:cart:drawer-closed')); changed = false; }
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
  }
  function render(html, focusMemo = rememberFocus()) {
    const node = drawer();
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#TorqueCartDrawer');
    if (!next) throw new Error(labels().error);
    const content = node.querySelector('[data-drawer-content]');
    const nextContent = next.querySelector('[data-drawer-content]');
    if (window.TorqueIntegrations) window.TorqueIntegrations.replaceContent(content, nextContent);
    else content.replaceChildren(...nextContent.childNodes);
    shuffleCombos(node);
    window.TorqueCartGroups?.render(node);
    node.querySelector('[data-drawer-count]').textContent = next.querySelector('[data-drawer-count]').textContent;
    const count = node.querySelector('[data-drawer-count]').textContent;
    document.querySelectorAll('#cartn, [data-cart-count]').forEach(badge => { badge.textContent = count; });
    currentCodes = (node.querySelector('[data-drawer-pairs]')?.dataset.drawerCodes || '').split(',').map(code => code.trim()).filter(Boolean);
    needsRecovery = false;
    node.querySelector('[data-drawer-retry]')?.remove();
    updateBusyControls();
    if (focusMemo && !restoreFocus(focusMemo)) pendingFocus = focusMemo;
    window.TorqueIntegrations?.rendered(node, 'drawer');
    setTimeout(() => void applyPairCode(), 0);
  }
  async function refresh(focusMemo = rememberFocus()) {
    const node = drawer();
    if (!node) return;
    const version = ++refreshVersion;
    const revision = window.TorqueIntegrations?.cart?.revision;
    try {
      const response = await fetch(`${endpoint('cart')}?section_id=${encodeURIComponent(node.dataset.sectionId)}`, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(labels().error);
      const html = await response.text();
      if (version !== refreshVersion || drawer() !== node) return;
      if (revision !== window.TorqueIntegrations?.cart?.revision) {
        scheduleRefresh();
        return;
      }
      render(html, focusMemo);
    } catch (error) {
      if (version === refreshVersion && drawer() === node) {
        needsRecovery = true;
        updateBusyControls();
        status(labels().error, true);
      }
      throw error;
    }
  }
  async function showAdded(celebrate = false, item = null, focusMemo = null) {
    if (!drawer()) return;
    if (!themeMode()) { presentExternal('add'); return; }
    const addedItem = celebrate ? item || latestAddedItem : null;
    if (celebrate) latestAddedItem = null;
    changed = true;
    open();
    status(labels().loading);
    try { await refresh(focusMemo); status(labels().added); if (celebrate) celebrateAdd(addedItem); }
    catch { status(labels().error, true); }
  }
  function mutate(path, body, operation = 'quantity', focusMemo = rememberFocus()) {
    return runCartTask(async () => {
      let confirmed = false;
      const target = drawer();
      ++refreshVersion;
      setBusy(true);
      status(labels().loading);
      try {
        const request = typeof body === 'function' ? body() : body;
        const response = await fetch(endpoint(path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...request, sections: drawer().dataset.sectionId, sections_url: endpoint('cart') }) });
        const cart = await response.json();
        if (!response.ok) throw new Error(typeof cart.description === 'string' ? cart.description : labels().error);
        confirmed = true;
        publish('cart:change', { source: 'drawer', operation, cart });
        if (drawer() !== target) return;
        changed = true;
        await refresh(focusMemo);
        status(labels().updated);
      } catch (error) {
        publish(confirmed ? 'cart:render-error' : 'cart:error', { source: 'drawer', operation, reason: 'request-failed' });
        if (!confirmed) {
          try { await refresh(focusMemo); changed = true; } catch {}
          window.TorqueIntegrations?.cart?.invalidate(undefined, 'drawer');
        }
        status(error.message || labels().error, true);
      }
      finally { setBusy(false); }
    });
  }
  document.addEventListener('click', event => {
    const node = drawer();
    if (!node) return;
    if (event.target.closest('[data-drawer-close]')) { close(); return; }
    if (event.target.closest('[data-drawer-retry]')) {
      if (!busy && cartPending() === 0) void runCartTask(async () => {
        status(labels().loading);
        try { await refresh(); status(''); } catch { status(labels().error, true); }
      });
      return;
    }
    if (locked() && event.target.closest('.tq-drawer__checkout')) { event.preventDefault(); return; }
    if (event.target === node) {
      const rect = node.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      return;
    }
    const quantity = event.target.closest('[data-drawer-quantity]');
    if (quantity) {
      if (!locked()) {
        const line = quantity.closest('[data-drawer-key]');
        const count = Number(quantity.dataset.drawerQuantity);
        const request = window.TorqueCartGroups?.request(line, count) || { id: line.dataset.drawerKey, quantity: count };
        void mutate(request.updates ? 'cart/update.js' : 'cart/change.js', request);
      }
      return;
    }
    const link = event.target.closest('a.hic');
    if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0 && new URL(link.href).pathname === node.dataset.cartUrl) {
      if (!themeMode()) { event.preventDefault(); presentExternal('cart-link'); return; }
      event.preventDefault(); open(); status(labels().loading);
      void refresh().then(() => status('')).catch(() => status(labels().error, true));
    }
  });
  document.addEventListener('cancel', event => {
    if (event.target === drawer()) { event.preventDefault(); close(); }
  }, true);
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (event.defaultPrevented || !form.action || !new URL(form.action).pathname.endsWith('/cart/add')) return;
    if (!themeMode()) return; // Keep ordinary native forms available to the app/cart page.
    event.preventDefault();
    if (form.dataset.drawerAdding) return;
    const data = new FormData(form);
    data.delete('return_to');
    const focusMemo = rememberFocus() || rememberFocus(event.submitter);
    const source = form.closest('[data-drawer-combo]') ? 'drawer:frame' : form.closest('#TorqueCartDrawer') ? 'drawer:merch' : form.closest('[data-cart-root]') ? 'cart-page:merch' : 'product-form';
    form.dataset.drawerAdding = 'true';
    const button = event.submitter;
    const wasDisabled = button?.disabled;
    if (button) button.disabled = true;
    let errorNode = form.querySelector('[data-merch-error]');
    if (errorNode) errorNode.textContent = '';
    await runCartTask(async () => {
      setBusy(true);
      try {
        const response = await fetch(endpoint('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const payload = await response.json();
        if (!response.ok) throw new Error(typeof payload.description === 'string' ? payload.description : labels().error);
        publish('cart:add', { source, requestedQuantity: Number(data.get('quantity') || 1), items: payload.items || [payload] });
        await showAdded(true, payload.items?.[0] || payload, focusMemo);
      } catch (error) {
        publish('cart:error', { source, operation: 'add', reason: 'request-failed' });
        try { await refresh(focusMemo); changed = true; } catch {}
        window.TorqueIntegrations?.cart?.invalidate(undefined, source);
        if (!errorNode) { errorNode = document.createElement('p'); errorNode.dataset.merchError = ''; errorNode.setAttribute('role', 'alert'); form.append(errorNode); }
        errorNode.textContent = error.message || labels().error;
        status(error.message || labels().error, true);
      } finally {
        delete form.dataset.drawerAdding;
        setBusy(false);
        if (button) {
          // A shopper may change a quick-buy option while the request is pending.
          // Its current add label reflects availability; keep that state intact.
          const quickBuyAdd = button.querySelector('[data-drive-buy-add]');
          const selected = form.querySelector('select[name="id"]')?.selectedOptions[0];
          button.disabled = Boolean(wasDisabled || quickBuyAdd?.hidden || (selected?.value && selected.disabled));
        }
      }
    });
  });
  document.addEventListener('torque:commerce', event => {
    if (event.detail?.type === 'cart:add') latestAddedItem = event.detail.items?.[0] || null;
  });
  document.addEventListener('cart:updated', event => {
    if (Array.isArray(event.detail?.cart?.discount_codes)) currentCodes = event.detail.cart.discount_codes.map(discount => discount.code).filter(Boolean);
    if (event.detail?.source === 'cart-page') scheduleRefresh();
    if (event.detail?.item) void showAdded(true, event.detail.item);
  });
  document.addEventListener('torque:cart:queue', updateBusyControls);
  document.addEventListener('torque:cart:added', () => void showAdded(true));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.contains(drawer())) { ++refreshVersion; clearTimeout(closeTimer); clearConfetti(); document.body.style.overflow = previousOverflow; }
  });
  document.addEventListener('shopify:section:load', event => {
    const node = drawer();
    if (node && event.target.contains(node)) { shuffleCombos(node); window.TorqueCartGroups?.render(node); window.TorqueIntegrations?.rendered(node, 'drawer'); }
  });
  shuffleCombos(drawer());
  window.TorqueCartGroups?.render(drawer());
  void applyPairCode();
  if (drawer()) window.TorqueIntegrations?.rendered(drawer(), 'drawer');
  window.torqueCartDrawer = { open: showAdded, close, refresh };
})();
